// External libraries
import React, { useState, useMemo } from "react";
import Select from "react-select";

// Project components
import Button from "../../../../../Components/Button";
import Paginator from "../../../../../Components/Paginator";
import TableLite from "../../../../../Components/TableLite";

// Feature/context-specific hooks and constants
import { useSeedConfigurations } from "../../../../../Api/hooks";

/**
 * Table component for selecting, copying, and deleting seed configurations
 * Displays configurations with pagination and action buttons
 * @param {Object} props - Component props
 * @param {Function} props.onEdit - Callback function when edit button is clicked
 * @param {Function} props.onRun - Callback function when run button is clicked
 * @param {Function} props.onCopy - Callback function when copy button is clicked
 * @param {Function} props.onDelete - Callback function when delete button is clicked
 * @returns {JSX.Element} The select configuration table component
 */
const SelectConfigTable = ({ onEdit, onRun, onCopy, onDelete }) => {
	const { data, isLoading, error, refetch } = useSeedConfigurations();
	const [currentPage, setCurrentPage] = useState(1);
	const itemsPerPage = 5;

	// Safely extract configurations array
	const configurations = data?.data?.data || [];

	// Transform the data for the table
	const tableData = useMemo(() => {
		return configurations.map((seedConfiguration) => {
			// Safety check for seedConfiguration structure
			if (!seedConfiguration || !seedConfiguration.seedConfiguration) {
				console.warn("Invalid seed configuration structure:", seedConfiguration);
				return {
					id: seedConfiguration?._id || "unknown",
					config_name: "Invalid Configuration",
					numberOfEmployees: 0,
					numberOfVersions: 0,
					...seedConfiguration,
				};
			}

			const configData = seedConfiguration.seedConfiguration;

			// Calculate number of employees + number of random employees

			const environment = configData.environment;

			const numberOfCustomEmployees = Array.isArray(configData.employees) ? configData.employees.length : 0;
			const numberOfRandomEmployees = configData.random_seeds
				? Number(configData.random_seeds.employee_tree_count || 0) *
					Number(configData.random_seeds.employee_branch_per_tree_count || 0)
				: 0;

			const numberOfAdmins =
				Number(configData.random_seeds?.admin || 0) +
				Number(configData.random_seeds?.billing_admin || 0) +
				Number(configData.random_seeds?.basic_admin || 0);

			const totalNumberOfEmployees = numberOfCustomEmployees + numberOfRandomEmployees + numberOfAdmins;

			//get the number of versions
			const versionCount = seedConfiguration.version;

			const numberOfGoals = configData.goals.length + configData.random_seeds.goals;
			const numberOfReviewForms = configData.review_forms.length + configData.random_seeds.review_forms;
			const configurationOptions =
				configData.company_functions.length > 0
					? configData.company_functions.map((option) => option.split(" ")[0].slice(0, -1)).join(", ")
					: "None";

			const transformedRow = {
				id: seedConfiguration._id,
				config_name: configData.config_name || "Unnamed Configuration",
				environment,
				numberOfEmployees: totalNumberOfEmployees,
				numberOfVersions: versionCount,
				numberOfGoals,
				numberOfReviewForms,
				configurationOptions,
				...seedConfiguration,
			};

			return transformedRow;
		});
	}, [configurations]);

	if (configurations.length === 0) {
		return <div>Create a new configuration to get started</div>;
	}

	// Calculate pagination values
	const totalItems = tableData.length;
	const totalPages = Math.ceil(totalItems / itemsPerPage);
	const startIndex = (currentPage - 1) * itemsPerPage;
	const endIndex = startIndex + itemsPerPage;
	const currentPageData = tableData.slice(startIndex, endIndex);

	/**
	 * Handle pagination page change
	 * @param {number} page - The page number to navigate to
	 */
	const handlePageChange = (page) => {
		setCurrentPage(page);
	};

	// Handle loading state
	if (isLoading) {
		return <div>Loading configurations...</div>;
	}

	// Handle error state
	if (error) {
		return <div>Error loading configurations: {error.message}</div>;
	}

	const columns = [
		{
			id: "configName",
			label: "Config Name",
			getter: (row) => {
				return <div style={{ fontWeight: "bold" }}>{row.config_name}</div>;
			},
		},
		{
			id: "environment",
			label: "Environment",
			getter: (row) => {
				return <div>{row.environment}</div>;
			},
		},
		{
			id: "numberOfEmployees",
			label: "Employees",
			getter: (row) => {
				return <div>{row.numberOfEmployees}</div>;
			},
		},
		{
			id: "numberOfGoals",
			label: "Goals",
			getter: (row) => {
				return <div>{row.numberOfGoals}</div>;
			},
		},
		{
			id: "numberOfReviewForms",
			label: "Review Forms",
			getter: (row) => {
				return <div>{row.numberOfReviewForms}</div>;
			},
		},
		{
			id: "configurationOptions",
			label: "Configuration Options",
			getter: (row) => {
				return <div>{row.configurationOptions}</div>;
			},
		},
		{
			id: "numberOfVersions",
			label: "Versions",
			getter: (row) => {
				return <div>{row.numberOfVersions}</div>;
			},
		},
		{
			id: "actions",
			label: "Actions",
			width: "200px",
			getter: (row) => {
				const actionOptions = [
					{ value: "run", label: "Run" },
					{ value: "edit", label: "Edit" },
					{ value: "copy", label: "Copy" },
					{ value: "delete", label: "Delete" },
				];

				const [selectedAction, setSelectedAction] = React.useState(null);

				const handleActionChange = (selected) => {
					setSelectedAction(null); // Reset after selection
					if (!selected) return;
					switch (selected.value) {
						case "run":
							onRun?.({ seedConfigurationId: row.id });
							break;
						case "edit":
							onEdit?.({ seedConfigurationId: row.id });
							break;
						case "copy":
							onCopy?.({ seedConfigurationId: row.id });
							break;
						case "delete":
							onDelete?.({ seedConfigurationId: row.id });
							break;
						default:
							break;
					}
				};

				return (
					<div style={{ minWidth: 120 }}>
						<Select
							value={selectedAction}
							onChange={handleActionChange}
							options={actionOptions}
							placeholder="Select Action"
							isClearable={false}
							menuPlacement="auto"
							styles={{
								container: (base) => ({ ...base, minWidth: 120 }),
								control: (base) => ({ ...base, minHeight: 32, height: 32, fontSize: 14 }),
							}}
						/>
					</div>
				);
			},
		},
	];

	return (
		<div>
			<TableLite rows={currentPageData || []} columns={columns} emptyMessage="No seed configurations found" />

			{totalPages > 1 && (
				<div style={{ marginTop: "20px", display: "flex", justifyContent: "center" }}>
					<Paginator
						currentPage={currentPage}
						pageCount={totalPages}
						onClick={handlePageChange}
						showSlider={false}
						isRequesting={isLoading}
					/>
				</div>
			)}

			{totalItems > 0 && (
				<div style={{ marginTop: "10px", textAlign: "center", fontSize: "14px", color: "#666" }}>
					Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} configurations
				</div>
			)}
		</div>
	);
};

export default SelectConfigTable;
